'use client';

import LaunchIcon from '@mui/icons-material/Launch';
import { Box, Button, type SxProps, Typography } from '@mui/material';
import type { CtaType } from '@repo/segment/Analytics/types';
import { CtaLink } from '@repo/segment/components/CtaLink';
import { LinkType, MonetizableEnum } from '@repo/segment/enums';
import type { AnalyticsType } from '@repo/segment/types';
import { EntityPlacementCtaTypeEnum } from '@repo/supergraph/marketplace/enums';
import { AccessibleHiddenText } from '@repo/ui/AccessibleHiddenText';
import { CtaVariantStyles, CtaVariantsEnum } from './variants';

const MAXIMUM_COLUMNS_MOBILE = 2;

export type CtaButtonProps = {
  /**
   * The CTA configuration object
   */
  cta?: CtaType;
  /**
   * The CTA configuration object
   */
  variant?: CtaVariantsEnum;
  /**
   * Analytics payload for tracking
   */
  analyticsPayload: AnalyticsType;
  /**
   * Optional custom styles to override default button styling
   */
  sx?: SxProps;
  /**
   * Optional flag to indicate if the button should be full width
   */
  fullWidth?: boolean;
  /**
   * Optional string to indicate if federally insured annotation should be shown
   */
  federallyInsuredAnnotation?: string;
  /**
   * Optional index for comparison table columns to control mobile visibility
   */
  columnIndex?: number;
  /**
   * Optional flag to use CD rates specific styling for subtext and federally insured annotation
   */
  useCdRatesStyle?: boolean;
  /**
   * Optional override for the button styles
   */
  buttonStylesOverride?: SxProps;
  /**
   * Optional flag to hide the subtext
   */
  hideSubtext?: boolean;
  /**
   * Optional override for the subtext styles
   */
  subtextStylesOverride?: SxProps;
  /**
   * Optional override for the impression_section value from the SKU viewed context
   */
  impressionSectionOverride?: string;
  /**
   * Optional flag to hide the external-link icon, regardless of monetizable/cta type
   */
  hideIcon?: boolean;
};

export const CtaButton = ({
  cta,
  variant = CtaVariantsEnum.Primary,
  analyticsPayload,
  sx = {},
  fullWidth = false,
  federallyInsuredAnnotation = '',
  columnIndex,
  useCdRatesStyle = false,
  buttonStylesOverride = {},
  hideSubtext = false,
  subtextStylesOverride = {},
  impressionSectionOverride,
  hideIcon = false,
}: CtaButtonProps) => {
  if (!cta) {
    return null;
  }

  let ctaSubtext = cta.subtext;
  if (cta.subtext && federallyInsuredAnnotation && !useCdRatesStyle) {
    ctaSubtext = `${cta.subtext}, ${federallyInsuredAnnotation}`;
  }

  // Handle mobile display for comparison table columns
  const mobileDisplay =
    typeof columnIndex === 'number'
      ? columnIndex > MAXIMUM_COLUMNS_MOBILE - 1
        ? 'none'
        : 'flex'
      : undefined;

  const variantStyle = CtaVariantStyles[variant];

  return (
    <Box
      sx={{
        display: mobileDisplay ? { xs: mobileDisplay, sm: 'flex' } : 'flex',
        flexDirection: 'column',
        gap: 1,
        textAlign: 'center',
        width: fullWidth ? '100%' : 'auto',
      }}
    >
      <Box data-testid="cta-container">
        <CtaLink
          cta={cta}
          monetizable={analyticsPayload.monetizable}
          analyticsPayload={analyticsPayload}
          Component={Button}
          linkType={LinkType.APPLY_NOW_BUTTON}
          impressionSectionOverride={impressionSectionOverride}
          sx={[
            ...(Array.isArray(variantStyle) ? variantStyle : [variantStyle]),
            {
              whiteSpace: 'nowrap',
              height: 45,
              width: '100%',
              lineHeight: 1,
              padding: { xs: '8px 16px', md: '16px' },
              letterSpacing: '0.8125px',
              justifyContent: 'center',
              ...buttonStylesOverride,
            },
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
          data-testid="cta-button"
        >
          {cta.text}
          {(analyticsPayload.monetizable === MonetizableEnum.yesAssumed ||
            cta.type === EntityPlacementCtaTypeEnum.ApplyNow) && (
            <>
              <AccessibleHiddenText component="span">on partner's site</AccessibleHiddenText>
              {!hideIcon && <LaunchIcon sx={{ display: 'inline-block', marginLeft: 0.5 }} />}
            </>
          )}
        </CtaLink>
        {((ctaSubtext && !hideSubtext) || (useCdRatesStyle && federallyInsuredAnnotation)) && (
          <Box
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
              lineHeight: 0,
              width: useCdRatesStyle ? { xs: '100%', sm: 'auto' } : 'auto',
            }}
            data-testid="cta-subtext"
          >
            {useCdRatesStyle ? (
              <>
                {ctaSubtext && (
                  <Typography
                    variant="textSmall"
                    sx={{
                      align: 'center',
                      fontSize: 11,
                      marginX: 1,
                      color: 'neutral.darker',
                      whiteSpace: 'wrap',
                      lineHeight: '24px',
                    }}
                  >
                    {ctaSubtext}
                    {federallyInsuredAnnotation ? ',' : ''}
                  </Typography>
                )}
                {federallyInsuredAnnotation && (
                  <Box sx={{ backgroundColor: '#FFFBF4', borderRadius: '0.5rem' }}>
                    <Typography
                      variant="textSmall"
                      sx={{
                        align: 'center',
                        fontSize: 11,
                        marginX: 1,
                        color: 'neutral.darker',
                        whiteSpace: 'wrap',
                      }}
                    >
                      {federallyInsuredAnnotation}
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              <Typography
                variant="textSmall"
                align="center"
                sx={{
                  marginRight: 1,
                  color: 'neutral.dark',
                  whiteSpace: 'wrap',
                  lineHeight: '24px',
                  ...subtextStylesOverride,
                }}
              >
                {ctaSubtext}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
